/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/**
 * Menu WYSIWYG Block Frontend Functionality
 * 
 * Handles tab navigation and Gravity Forms integration within Menu WYSIWYG blocks:
 * - Tab switching functionality
 * - Adds 'gf-filled' class to text/email fields with values
 * - Repositions labels for custom-input fields to appear below inputs
 */

(function () {
    'use strict';

    // Configuration
    const CONFIG = {
        wysiwygSelector: '.wp-block-block-builder-by-arc-menu-wysiwyg',
        tabSelector: '.menu-wysiwyg__tab',
        tabsSelector: '.menu-wysiwyg__tabs',
        headerSelector: '.site-header',
        scrollGap: 16,
        containerSelector: '.menu-item-container',
        fieldSelector: '.gfield-custom-input input[type="text"], .ginput-container input[type="email"]',
        customInputSelector: '.gfield-custom-input',
        filledClass: 'gf-filled',
        labelSelector: '.gfield_label',
        inputContainerSelector: '.ginput_container'
    };

    /**
     * Updates the 'gf-filled' class based on field value
     * @param {HTMLElement} field - The input field element
     */
    function updateFieldClass(field) {
        const hasValue = field.value.trim().length > 0;

        if (hasValue) {
            field.classList.add(CONFIG.filledClass);
        } else {
            field.classList.remove(CONFIG.filledClass);
        }
    }

    /**
     * Repositions labels for custom-input fields to appear below inputs
     */
    function repositionLabels() {
        const wysiwygBlocks = document.querySelectorAll(CONFIG.wysiwygSelector);

        wysiwygBlocks.forEach(function (wysiwygBlock) {
            const customInputFields = wysiwygBlock.querySelectorAll(CONFIG.customInputSelector);

            customInputFields.forEach(function (fieldContainer) {
                const label = fieldContainer.querySelector(CONFIG.labelSelector);
                const inputContainer = fieldContainer.querySelector(CONFIG.inputContainerSelector);

                // Only move label if it exists and is currently outside the input container
                if (label && inputContainer && !inputContainer.contains(label)) {
                    inputContainer.appendChild(label);
                }
            });
        });
    }

    /**
     * Initializes all field states on page load
     */
    function initializeFields() {
        const wysiwygBlocks = document.querySelectorAll(CONFIG.wysiwygSelector);

        wysiwygBlocks.forEach(function (wysiwygBlock) {
            const fields = wysiwygBlock.querySelectorAll(CONFIG.fieldSelector);
            fields.forEach(updateFieldClass);
        });
    }

    /**
     * Handles input events for real-time field class updates
     * @param {Event} event - The input event
     */
    function handleInputEvent(event) {
        const wysiwygBlock = event.target.closest(CONFIG.wysiwygSelector);

        if (wysiwygBlock && event.target.matches(CONFIG.fieldSelector)) {
            updateFieldClass(event.target);
        }
    }

    /**
     * Handles change events for field class updates
     * @param {Event} event - The change event
     */
    function handleChangeEvent(event) {
        const wysiwygBlock = event.target.closest(CONFIG.wysiwygSelector);

        if (wysiwygBlock && event.target.matches(CONFIG.fieldSelector)) {
            updateFieldClass(event.target);
        }
    }

    /**
     * Handles tab switching functionality
     * @param {Event} event - The click event
     */
    function handleTabClick(event) {
        const tab = event.target;
        const tabId = tab.getAttribute('data-tab-id');

        if (!tabId) return;

        activateTab(tab);

        // Reflect the open tab in the URL so it can be copied and shared,
        // without adding a history entry or jumping the page
        history.replaceState(null, '', '#' + tabId);
    }

    /**
     * Shows the container for a tab and marks the tab active, within its own block
     * @param {HTMLElement} tab - The tab button
     */
    function activateTab(tab) {
        const tabId = tab.getAttribute('data-tab-id');
        const wysiwygBlock = tab.closest(CONFIG.wysiwygSelector);

        // Hide all containers
        wysiwygBlock.querySelectorAll(CONFIG.containerSelector).forEach(container => {
            container.style.display = 'none';
        });

        // Show selected container
        const targetContainer = document.getElementById(tabId);
        if (targetContainer) {
            targetContainer.style.display = 'block';
        }

        // Update active tab
        wysiwygBlock.querySelectorAll(CONFIG.tabSelector).forEach(tabBtn => {
            tabBtn.classList.remove('active');
        });
        tab.classList.add('active');
    }

    /**
     * Finds the tab whose data-tab-id matches the URL hash
     * @return {HTMLElement|null} The matching tab button, or null
     */
    function findTabFromHash() {
        let tabId;

        try {
            tabId = decodeURIComponent(window.location.hash.slice(1));
        } catch (e) {
            return null;
        }

        if (!tabId) return null;

        const tabs = document.querySelectorAll(CONFIG.wysiwygSelector + ' ' + CONFIG.tabSelector);
        return Array.prototype.find.call(tabs, tab => tab.getAttribute('data-tab-id') === tabId) || null;
    }

    /**
     * Scrolls the tab bar into view, below the sticky site header
     * @param {HTMLElement} tab - The tab button
     */
    function scrollToTabs(tab) {
        const tabBar = tab.closest(CONFIG.tabsSelector) || tab;
        const header = document.querySelector(CONFIG.headerSelector);
        const headerHeight = header ? header.offsetHeight : 0;
        const top = tabBar.getBoundingClientRect().top + window.scrollY - headerHeight - CONFIG.scrollGap;

        window.scrollTo({ top: Math.max(top, 0) });

        // The tab bar scrolls sideways on small screens, so centre the open tab in it
        if (tabBar !== tab) {
            const tabOffset = tab.getBoundingClientRect().left - tabBar.getBoundingClientRect().left;
            tabBar.scrollLeft += tabOffset - (tabBar.clientWidth - tab.offsetWidth) / 2;
        }
    }

    /**
     * Opens and scrolls to the tab named in the URL hash, if any
     * @return {HTMLElement|null} The opened tab, or null
     */
    function openTabFromHash() {
        const tab = findTabFromHash();

        if (!tab) return null;

        activateTab(tab);
        scrollToTabs(tab);

        return tab;
    }

    /**
     * Re-applies the tab scroll once the page has loaded. The hash also matches the
     * tab's container id, so the browser's own jump to that container lands after
     * ours, just below the tabs. Skipped if the visitor has started scrolling.
     * @param {HTMLElement} tab - The tab button
     */
    function settleScrollAfterLoad(tab) {
        let userScrolled = false;
        const markUserScrolled = () => { userScrolled = true; };

        ['wheel', 'touchstart', 'keydown'].forEach(eventName => {
            window.addEventListener(eventName, markUserScrolled, { once: true, passive: true });
        });

        const settle = () => setTimeout(() => {
            if (!userScrolled) scrollToTabs(tab);
        }, 0);

        if (document.readyState === 'complete') {
            settle();
        } else {
            window.addEventListener('load', settle, { once: true });
        }
    }

    /**
     * Initialize tab functionality
     */
    function initializeTabs() {
        const wysiwygBlocks = document.querySelectorAll(CONFIG.wysiwygSelector);

        wysiwygBlocks.forEach(function (wysiwygBlock) {
            const tabs = wysiwygBlock.querySelectorAll(CONFIG.tabSelector);
            
            tabs.forEach(function (tab) {
                tab.addEventListener('click', handleTabClick);
            });

            // Hide all containers except the first one
            const containers = wysiwygBlock.querySelectorAll(CONFIG.containerSelector);
            containers.forEach(function (container, index) {
                if (index === 0) {
                    container.style.display = 'block';
                } else {
                    container.style.display = 'none';
                }
            });
        });
    }

    /**
     * Initialize functionality when DOM is ready
     */
    function init() {
        initializeTabs();

        const hashTab = openTabFromHash();
        if (hashTab) {
            settleScrollAfterLoad(hashTab);
        }

        initializeFields();
        repositionLabels();

        document.addEventListener('input', handleInputEvent);
        document.addEventListener('change', handleChangeEvent);
        window.addEventListener('hashchange', openTabFromHash);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
